/**
 * MyCopro - Document Store (Zustand + Firestore + Firebase Storage)
 * Avec validation sécurisée des fichiers et notifications harmonisées
 * ✅ NOUVEAU: Pagination Firestore (limit + startAfter) — chargement progressif
 * src/stores/documentStore.ts
 */

import { create } from 'zustand';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '@/config/firebase';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

export type DocumentCategory = 'ag' | 'general' | 'travaux' | 'contrats' | 'autre';
export type DocumentAudience = 'all' | 'owners' | 'council';

export interface Document {
  id: string;
  title: string;
  description?: string;
  category: DocumentCategory;
  audience: DocumentAudience;
  fileName: string;
  fileSize: string;
  fileSizeBytes: number;
  fileType: string;
  fileUrl: string;
  storagePath: string;
  uploadedAt: string;
  uploadedBy: string;
  uploadedByName: string;
  residenceId: string;
}

export interface DocumentUploadData {
  title: string;
  description?: string;
  category: DocumentCategory;
  audience: DocumentAudience;
  uploadedBy: string;
  uploadedByName: string;
}

// ============================================================================
// CONSTANTS - SÉCURITÉ FICHIERS
// ============================================================================

// Taille maximale : 10 MB
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Types MIME autorisés (sécurisés) - PDF, Word, Excel, Images JPG/PNG uniquement
export const ALLOWED_MIME_TYPES: Record<string, string[]> = {
  // Documents PDF
  'application/pdf': ['.pdf'],
  
  // Images (JPG et PNG uniquement)
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  
  // Documents Word
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  
  // Documents Excel
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

// Extensions dangereuses à bloquer absolument
export const BLOCKED_EXTENSIONS = [
  // Exécutables Windows
  '.exe', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif',
  // Scripts
  '.js', '.vbs', '.vbe', '.jse', '.ws', '.wsf', '.wsc', '.wsh',
  '.ps1', '.psm1', '.psd1', // PowerShell
  '.py', '.pyw', // Python
  '.php', '.php3', '.php4', '.php5', '.phtml',
  '.pl', '.pm', // Perl
  '.rb', // Ruby
  '.sh', '.bash', '.zsh', '.csh', // Shell Unix
  // Web dangereux
  '.html', '.htm', '.xhtml', '.svg', '.xml',
  '.asp', '.aspx', '.jsp', '.jspx',
  // Java/Jar
  '.jar', '.war', '.ear', '.class',
  // Macros Office
  '.docm', '.xlsm', '.pptm', '.dotm', '.xltm', '.potm',
  // Autres dangereux
  '.hta', '.cpl', '.msc', '.inf', '.reg', '.dll', '.sys',
  '.lnk', '.url', '.iso', '.img',
];

// Labels des catégories
export const DOCUMENT_CATEGORIES: Record<DocumentCategory, { label: string; color: string }> = {
  ag: { label: 'Assemblées Générales', color: 'bg-blue-100 text-blue-700' },
  general: { label: 'Documents Généraux', color: 'bg-emerald-100 text-emerald-700' },
  travaux: { label: 'Travaux', color: 'bg-orange-100 text-orange-700' },
  contrats: { label: 'Contrats', color: 'bg-indigo-100 text-indigo-700' },
  autre: { label: 'Autre', color: 'bg-gray-100 text-gray-700' }
};

export const AUDIENCE_OPTIONS: Record<DocumentAudience, { label: string; description: string }> = {
  all: { label: 'Tous les résidents', description: 'Locataires et copropriétaires' },
  owners: { label: 'Copropriétaires', description: 'Copropriétaires et conseil syndical' },
  council: { label: 'Conseil syndical', description: 'Membres du conseil uniquement' }
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Valide un fichier avant upload
 */
export const validateFile = (file: File): FileValidationResult => {
  // 1. Vérifier la taille
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `Fichier trop volumineux (${sizeMB} MB). Maximum autorisé : 10 MB`
    };
  }

  // 2. Vérifier l'extension (double vérification)
  const fileName = file.name.toLowerCase();
  const extension = '.' + fileName.split('.').pop();
  
  // Bloquer les extensions dangereuses
  if (BLOCKED_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `Type de fichier non autorisé (${extension}). Ce type de fichier peut contenir du code malveillant.`
    };
  }

  // 3. Vérifier le type MIME
  const mimeType = file.type.toLowerCase();
  if (!ALLOWED_MIME_TYPES[mimeType]) {
    return {
      valid: false,
      error: `Type de fichier non autorisé (${mimeType || 'inconnu'}). Types acceptés : PDF, Word, Excel, images JPG/PNG.`
    };
  }

  // 4. Vérifier la cohérence extension/MIME
  const allowedExtensions = ALLOWED_MIME_TYPES[mimeType];
  if (!allowedExtensions.some(ext => fileName.endsWith(ext))) {
    return {
      valid: false,
      error: `Incohérence entre le type de fichier et son extension. Fichier potentiellement dangereux.`
    };
  }

  // 5. Vérifier que le nom de fichier ne contient pas de caractères suspects
  const suspiciousPatterns = [
    /\.\./,           // Path traversal
    /<script/i,       // XSS
    /javascript:/i,   // XSS
    /on\w+=/i,        // Event handlers
    /%00/,            // Null byte
    /[\x00-\x1f]/,    // Control characters
  ];
  
  if (suspiciousPatterns.some(pattern => pattern.test(file.name))) {
    return {
      valid: false,
      error: `Nom de fichier contenant des caractères non autorisés.`
    };
  }

  return { valid: true };
};

/**
 * Nettoyer le nom de fichier
 */
export const sanitizeFileName = (fileName: string): string => {
  // Garder uniquement les caractères alphanumériques, tirets, underscores et le point de l'extension
  const parts = fileName.split('.');
  const extension = parts.pop() || '';
  const baseName = parts.join('.');
  
  const sanitized = baseName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
    .replace(/[^a-zA-Z0-9_-]/g, '_') // Remplacer les caractères spéciaux
    .replace(/_+/g, '_')              // Éviter les underscores multiples
    .substring(0, 100);               // Limiter la longueur
  
  return `${sanitized}.${extension.toLowerCase()}`;
};

/**
 * Formater la taille du fichier
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// ============================================================================
// STORE
// ============================================================================

// ✅ Nombre de documents chargés par page
const PAGE_SIZE = 20;

interface DocumentState {
  documents: Document[];
  isLoading: boolean;
  uploadProgress: number;
  error: string | null;
  unsubscribe: Unsubscribe | null;
  
  // ✅ NOUVEAU: État pagination
  hasMore: boolean;                                    // true si d'autres documents existent
  isLoadingMore: boolean;                              // true pendant le chargement d'une page suivante
  lastDoc: QueryDocumentSnapshot<DocumentData> | null; // Curseur Firestore pour startAfter
  currentResidenceId: string | null;                   // Résidence active (pour loadMore)
  
  // Actions
  subscribeToDocuments: (residenceId: string) => void;
  unsubscribeFromDocuments: () => void;
  // ✅ NOUVEAU: Charger la page suivante
  loadMoreDocuments: () => Promise<void>;
  uploadDocument: (
    residenceId: string, 
    file: File, 
    metadata: DocumentUploadData
  ) => Promise<{ success: boolean; documentId?: string; error?: string }>;
  deleteDocument: (residenceId: string, documentId: string) => Promise<boolean>;
  clearDocuments: () => void;
}

export const useDocumentStore = create<DocumentState>((set, get) => ({
  documents: [],
  isLoading: false,
  uploadProgress: 0,
  error: null,
  unsubscribe: null,
  // ✅ NOUVEAU: État initial pagination
  hasMore: true,
  isLoadingMore: false,
  lastDoc: null,
  currentResidenceId: null,

  // ✅ MODIFIÉ: S'abonner aux documents en temps réel AVEC pagination
  // Charge les PAGE_SIZE premiers documents, puis loadMoreDocuments() pour la suite
  subscribeToDocuments: (residenceId: string) => {
    if (!residenceId) return;

    const { unsubscribe: currentUnsubscribe } = get();
    if (currentUnsubscribe) {
      currentUnsubscribe();
    }

    set({ isLoading: true, currentResidenceId: residenceId, hasMore: true, lastDoc: null });

    const docsRef = collection(db, 'residences', residenceId, 'documents');
    // ✅ PAGINATION: Limiter la requête initiale à PAGE_SIZE documents
    const q = query(docsRef, orderBy('uploadedAt', 'desc'), limit(PAGE_SIZE));

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const documents: Document[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title || '',
            description: data.description || '',
            category: data.category || 'autre',
            audience: data.audience || 'all',
            fileName: data.fileName || '',
            fileSize: data.fileSize || '',
            fileSizeBytes: data.fileSizeBytes || 0,
            fileType: data.fileType || '',
            fileUrl: data.fileUrl || '',
            storagePath: data.storagePath || '',
            uploadedAt: data.uploadedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            uploadedBy: data.uploadedBy || '',
            uploadedByName: data.uploadedByName || '',
            residenceId: residenceId
          };
        });

        // ✅ PAGINATION: Sauvegarder le dernier document comme curseur
        const lastDoc = snapshot.docs.length > 0 
          ? snapshot.docs[snapshot.docs.length - 1] 
          : null;

        // ✅ S'il y a moins de PAGE_SIZE résultats, il n'y a plus de pages
        const hasMore = snapshot.docs.length >= PAGE_SIZE;

        set({ documents, isLoading: false, error: null, lastDoc, hasMore });
      },
      (error) => {
        console.error('Error subscribing to documents:', error);
        set({ error: 'Erreur de chargement des documents', isLoading: false });
      }
    );

    set({ unsubscribe });
  },

  // ✅ NOUVEAU: Charger la page suivante (documents plus anciens)
  // Utilise getDocs (one-shot) avec startAfter pour ne pas multiplier les listeners
  loadMoreDocuments: async () => {
    const { currentResidenceId, lastDoc, hasMore, isLoadingMore } = get();
    
    if (!currentResidenceId || !lastDoc || !hasMore || isLoadingMore) return;

    set({ isLoadingMore: true });

    try {
      const docsRef = collection(db, 'residences', currentResidenceId, 'documents');
      const q = query(
        docsRef, 
        orderBy('uploadedAt', 'desc'), 
        startAfter(lastDoc), 
        limit(PAGE_SIZE)
      );

      const snapshot = await getDocs(q);

      const newDocuments: Document[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || '',
          description: data.description || '',
          category: data.category || 'autre',
          audience: data.audience || 'all',
          fileName: data.fileName || '',
          fileSize: data.fileSize || '',
          fileSizeBytes: data.fileSizeBytes || 0,
          fileType: data.fileType || '',
          fileUrl: data.fileUrl || '',
          storagePath: data.storagePath || '',
          uploadedAt: data.uploadedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          uploadedBy: data.uploadedBy || '',
          uploadedByName: data.uploadedByName || '',
          residenceId: currentResidenceId
        };
      });

      const newLastDoc = snapshot.docs.length > 0 
        ? snapshot.docs[snapshot.docs.length - 1] 
        : null;

      set(state => ({
        // ✅ Ajouter les nouveaux documents APRÈS les existants (dédupliqués)
        documents: [
          ...state.documents,
          ...newDocuments.filter(nd => !state.documents.some(d => d.id === nd.id))
        ],
        lastDoc: newLastDoc || state.lastDoc,
        hasMore: snapshot.docs.length >= PAGE_SIZE,
        isLoadingMore: false
      }));

      logger.db(` Loaded ${newDocuments.length} more documents`);
    } catch (error) {
      console.error('Error loading more documents:', error);
      set({ isLoadingMore: false });
    }
  },

  // Se désabonner
  unsubscribeFromDocuments: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
      set({ unsubscribe: null });
    }
  },

  // Uploader un document
  uploadDocument: async (residenceId, file, metadata) => {
    if (!residenceId) {
      return { success: false, error: 'Résidence non trouvée' };
    }

    // Valider le fichier
    const validation = validateFile(file);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    set({ uploadProgress: 0 });

    try {
      // Nettoyer et générer un nom unique
      const sanitizedName = sanitizeFileName(file.name);
      const uniqueName = `${Date.now()}_${sanitizedName}`;
      const storagePath = `residences/${residenceId}/documents/${uniqueName}`;
      
      // Upload vers Firebase Storage
      set({ uploadProgress: 20 });
      const storageRef = ref(storage, storagePath);
      
      // Métadonnées de sécurité
      const uploadMetadata = {
        contentType: file.type,
        customMetadata: {
          originalName: file.name,
          uploadedBy: metadata.uploadedBy,
          uploadedAt: new Date().toISOString()
        }
      };
      
      await uploadBytes(storageRef, file, uploadMetadata);
      set({ uploadProgress: 60 });
      
      // Obtenir l'URL de téléchargement
      const fileUrl = await getDownloadURL(storageRef);
      set({ uploadProgress: 80 });
      
      // Créer le document dans Firestore
      const docsRef = collection(db, 'residences', residenceId, 'documents');
      const docData = {
        title: metadata.title.trim(),
        description: metadata.description?.trim() || '',
        category: metadata.category,
        audience: metadata.audience,
        fileName: file.name,
        fileSize: formatFileSize(file.size),
        fileSizeBytes: file.size,
        fileType: file.type,
        fileUrl,
        storagePath,
        uploadedAt: Timestamp.now(),
        uploadedBy: metadata.uploadedBy,
        uploadedByName: metadata.uploadedByName
      };
      
      const docRef = await addDoc(docsRef, docData);
      set({ uploadProgress: 100 });

      // ✅ Créer une notification harmonisée pour les destinataires
      await createDocumentNotification(residenceId, {
        documentTitle: metadata.title,
        audience: metadata.audience,
        uploadedBy: metadata.uploadedBy,
        uploadedByName: metadata.uploadedByName
      });

      logger.db('Document uploaded with ID:', docRef.id);
      return { success: true, documentId: docRef.id };
      
    } catch (error) {
      console.error('Error uploading document:', error);
      return { success: false, error: 'Erreur lors de l\'upload du document' };
    } finally {
      set({ uploadProgress: 0 });
    }
  },

  // Supprimer un document
  deleteDocument: async (residenceId, documentId) => {
    if (!residenceId || !documentId) return false;

    try {
      // Récupérer le document pour avoir le storagePath
      const { documents } = get();
      const document = documents.find(d => d.id === documentId);
      
      // Supprimer le fichier de Storage
      if (document?.storagePath) {
        try {
          const storageRef = ref(storage, document.storagePath);
          await deleteObject(storageRef);
        } catch (e) {
          logger.storage('Could not delete file from storage:', e);
        }
      }

      // Supprimer le document de Firestore
      const docRef = doc(db, 'residences', residenceId, 'documents', documentId);
      await deleteDoc(docRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      return false;
    }
  },

  // Vider les documents
  clearDocuments: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
    }
    set({ documents: [], unsubscribe: null, hasMore: true, lastDoc: null, currentResidenceId: null });
  }
}));

// ============================================================================
// NOTIFICATION HELPER - FORMAT HARMONISÉ
// ============================================================================

/**
 * Crée une notification lors de l'ajout d'un document
 * Format harmonisé avec useNotifications.ts
 */
async function createDocumentNotification(
  residenceId: string, 
  data: {
    documentTitle: string;
    audience: DocumentAudience;
    uploadedBy: string;
    uploadedByName: string;
  }
): Promise<void> {
  try {
    const notificationsRef = collection(db, 'residences', residenceId, 'notifications');
    
    // Convertir audience en restrictedTo (format useNotifications)
    // restrictedTo vide = tous les utilisateurs
    let restrictedTo: string[] = [];
    
    switch (data.audience) {
      case 'all':
        // Pas de restriction = tous les utilisateurs
        restrictedTo = [];
        break;
      case 'owners':
        // Copropriétaires (owner, council, syndic, admin peuvent voir)
        restrictedTo = ['owner'];
        break;
      case 'council':
        // Conseil syndical uniquement
        restrictedTo = ['council'];
        break;
    }

    // Tronquer le titre si trop long
    const shortTitle = data.documentTitle.length > 50 
      ? data.documentTitle.substring(0, 47) + '...' 
      : data.documentTitle;

    const notificationData: Record<string, any> = {
      title: '📄 Nouveau document',
      message: `"${shortTitle}" ajouté par ${data.uploadedByName}`,
      type: 'info',
      createdAt: Timestamp.now(),
      sentBy: data.uploadedBy,
      sentByName: data.uploadedByName,
      readBy: [],
      link: '/documents' // Lien vers la page des documents
    };

    // Ajouter restrictedTo seulement si non vide
    if (restrictedTo.length > 0) {
      notificationData.restrictedTo = restrictedTo;
    }

    await addDoc(notificationsRef, notificationData);
    logger.db(' Notification sent for new document:', shortTitle);
  } catch (error) {
    console.error('[documentStore] Error creating document notification:', error);
    // Ne pas bloquer l'upload si la notification échoue
  }
}

export default useDocumentStore;
